import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Loader2, Save, Plus, Trash2, Upload, Edit3, Image as ImageIcon, Video,
  Facebook, Instagram, Youtube, Globe, Music
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * InlineArtistEditor — quick edit modal for an artist.
 * Lets the admin update photo, real_name, role, history, socials, videos
 * directly from the public Artists section.
 */
export default function InlineArtistEditor({ artist, adminPwd, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: artist?.name || '',
    real_name: artist?.real_name || artist?.realName || '',
    role: artist?.role || '',
    history: artist?.history || '',
    image_url: artist?.image && !artist.image.startsWith('/api/') ? artist.image : '',
    socials: { ...(artist?.socials || {}) },
    videos: Array.isArray(artist?.videos) ? [...artist.videos] : [],
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    artist?.image?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${artist.image}` : artist?.image || ''
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const SOCIAL_FIELDS = [
    { key: 'facebook',  icon: Facebook,  placeholder: 'facebook.com/...', color: 'text-blue-400' },
    { key: 'instagram', icon: Instagram, placeholder: 'instagram.com/...', color: 'text-pink-400' },
    { key: 'youtube',   icon: Youtube,   placeholder: 'youtube.com/@...', color: 'text-red-400' },
    { key: 'spotify',   icon: Music,     placeholder: 'open.spotify.com/...', color: 'text-green-400' },
    { key: 'website',   icon: Globe,     placeholder: 'https://...', color: 'text-yellow-400' },
  ];

  const detectPlatform = (url) => {
    const u = (url || '').toLowerCase();
    if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
    if (u.includes('facebook.com') || u.includes('fb.watch')) return 'facebook';
    if (u.includes('soundcloud.com')) return 'soundcloud';
    if (u.includes('vimeo.com')) return 'vimeo';
    if (u.includes('spotify.com')) return 'spotify';
    if (u.includes('tiktok.com')) return 'tiktok';
    return 'other';
  };

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setError('Image trop grande (max 10 MB)');
      return;
    }
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const updateSocial = (k, v) => setForm({ ...form, socials: { ...form.socials, [k]: v } });
  const addVideo = () => setForm({ ...form, videos: [...form.videos, { title: '', url: '', platform: 'youtube' }] });
  const updateVideo = (i, k, v) => setForm({
    ...form,
    videos: form.videos.map((x, idx) => idx === i ? { ...x, [k]: v, ...(k === 'url' ? { platform: detectPlatform(v) } : {}) } : x)
  });
  const removeVideo = (i) => setForm({ ...form, videos: form.videos.filter((_, idx) => idx !== i) });

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('password', adminPwd);
      fd.append('name', form.name);
      fd.append('real_name', form.real_name || '');
      fd.append('role', form.role);
      fd.append('history', form.history);
      fd.append('socials_json', JSON.stringify(form.socials || {}));
      fd.append('videos_json', JSON.stringify(form.videos || []));
      if (imageFile) {
        fd.append('image', imageFile);
      } else if (form.image_url) {
        fd.append('image_url', form.image_url);
      }
      const r = await fetch(`${API}/admin/artists/${artist.id}`, { method: 'PUT', body: fd });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError(d.detail || `Erreur ${r.status}`);
        return;
      }
      const updated = await r.json();
      onSaved && onSaved(updated);
      onClose();
    } catch (e) {
      setError(e.message || 'Erreur reseau');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="inline-artist-editor">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-950 border border-yellow-500/30 rounded-3xl w-full max-w-2xl my-8 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div>
            <p className="text-[10px] text-yellow-400 font-bold uppercase tracking-widest">Edition rapide</p>
            <h3 className="text-xl font-black text-white">{artist?.name}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-2">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Photo */}
          <section>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-1.5">
              <ImageIcon size={11} /> Photo
            </p>
            <div className="flex items-center gap-3">
              {imagePreview ? (
                <img src={imagePreview} alt="preview" className="w-20 h-20 rounded-xl object-cover border border-zinc-800" />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center">
                  <ImageIcon size={20} className="text-zinc-700" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <button onClick={() => fileRef.current?.click()}
                  data-testid="artist-edit-upload"
                  className="w-full px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-xs flex items-center justify-center gap-1.5">
                  <Upload size={11} /> Uploader une photo
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])} />
                <input value={form.image_url} onChange={(e) => { setForm({ ...form, image_url: e.target.value }); setImagePreview(e.target.value); }}
                  placeholder="ou colle une URL d'image"
                  className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-xs focus:outline-none focus:border-yellow-500" />
              </div>
            </div>
          </section>

          {/* Basic info */}
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Nom de scene</p>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Vrai nom</p>
              <input value={form.real_name} onChange={(e) => setForm({ ...form, real_name: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500" />
            </div>
            <div className="sm:col-span-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Role / Description</p>
              <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500" />
            </div>
            <div className="sm:col-span-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Histoire / Bio</p>
              <textarea value={form.history} onChange={(e) => setForm({ ...form, history: e.target.value })} rows={4}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500 resize-none" />
            </div>
          </section>

          {/* Socials */}
          <section>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Reseaux sociaux</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SOCIAL_FIELDS.map(s => (
                <div key={s.key} className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1">
                  <s.icon size={14} className={s.color} />
                  <input value={form.socials[s.key] || ''} onChange={(e) => updateSocial(s.key, e.target.value)}
                    placeholder={s.placeholder}
                    className="flex-1 bg-transparent text-white text-xs focus:outline-none" />
                </div>
              ))}
            </div>
          </section>

          {/* Videos */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Videos (YouTube, Facebook, SoundCloud...)</p>
              <button onClick={addVideo}
                data-testid="artist-edit-add-video"
                className="px-2 py-1 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 border border-yellow-500/30 rounded text-[10px] font-bold flex items-center gap-1">
                <Plus size={10} /> Ajouter
              </button>
            </div>
            {form.videos.length === 0 ? (
              <p className="text-[10px] text-zinc-600 italic px-3 py-2 bg-zinc-900/50 border border-dashed border-zinc-800 rounded-lg">
                Aucune video. Ajoute des liens YouTube, Facebook ou autres.
              </p>
            ) : (
              <div className="space-y-1.5">
                {form.videos.map((v, i) => (
                  <div key={i} className="flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 rounded-lg p-1.5">
                    <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${
                      v.platform === 'youtube' ? 'bg-red-500/20 text-red-400' :
                      v.platform === 'facebook' ? 'bg-blue-500/20 text-blue-400' :
                      v.platform === 'soundcloud' ? 'bg-orange-500/20 text-orange-400' :
                      'bg-zinc-700 text-zinc-400'
                    }`}>{v.platform}</span>
                    <input value={v.title} onChange={(e) => updateVideo(i, 'title', e.target.value)}
                      placeholder="Titre"
                      className="w-28 px-2 py-1 bg-black border border-zinc-800 rounded text-white text-[11px]" />
                    <input value={v.url} onChange={(e) => updateVideo(i, 'url', e.target.value)}
                      placeholder="https://..."
                      className="flex-1 px-2 py-1 bg-black border border-zinc-800 rounded text-white text-[11px]" />
                    <button onClick={() => removeVideo(i)}
                      className="p-1 text-red-400 hover:bg-red-500/20 rounded"><Trash2 size={11} /></button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {error && (
            <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-300">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-zinc-800">
          <button onClick={onClose}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-xs">Annuler</button>
          <button onClick={save} disabled={saving}
            data-testid="artist-edit-save"
            className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
            Sauvegarder
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
